import { NamespaceLoggerAdapter, sendTelemetryEvent } from '@fluidx/telemetry';
import { UserActivityTracker } from '@fluidx/utilities';
export class Heartbeat {
    constructor(logger) {
        this.heartbeatSendTimerId = undefined;
        /**
         * Listener for user activity state change
         * @param isActive Active state of the user
         */
        this.onUserActivityStateChanged = (isActive) => {
            // Resume the heartbeat as soon as the user becomes active
            if (isActive) {
                this.resume();
            }
            else {
                // Stop the heartbeat when user goes inactive
                this.stop();
            }
        };
        this.registerSessionEndingErrorObserver = (loadedInfo) => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            (async () => {
                try {
                    const resolvedLoadedInfo = await loadedInfo;
                    // If we fail to load the container/component or if the container closes post boot due to an error, dispose the heartbeat
                    resolvedLoadedInfo.component.catch(() => this.dispose());
                    resolvedLoadedInfo.container
                        .then((container) => {
                        container.on('closed', () => {
                            this.dispose();
                        });
                    })
                        .catch(() => {
                        this.dispose();
                    });
                }
                catch {
                    this.dispose();
                }
            })();
        };
        /** Method to stop(pause) the heartbeat */
        this.stop = () => {
            this.clearHeartbeatSendTimer();
        };
        /** Resume the heartbeat from stopped state, for example, when we pause the heartbeat in an idle session */
        this.resume = () => {
            this.sendHeartbeatAndReQueue();
        };
        /** Sets a timer to send a heartbeat after 5 minutes */
        this.queueHeartbeat = () => {
            // eslint-disable-next-line no-restricted-globals -- heartbeat will always be on "outside" of iframe (created explicitly by the host), so using "window" directly is fine
            this.heartbeatSendTimerId = window.setTimeout(() => {
                this.sendHeartbeatAndReQueue();
            }, Heartbeat.heartbeatIntervalMs);
        };
        /** Send the heartbeat and requeues another one to be send at the end of 5 minutes */
        this.sendHeartbeatAndReQueue = () => {
            // Log the telemetry heartbeat event
            this.heartbeatLogger && sendTelemetryEvent(this.heartbeatLogger, { eventName: 'Heartbeat' });
            this.queueHeartbeat();
        };
        /** When the heartbeat stops, clear all timers. This is done so when the heartbeat resumes, it starts afresh */
        this.clearHeartbeatSendTimer = () => {
            // eslint-disable-next-line no-restricted-globals -- heartbeat will always be on "outside" of iframe (created explicitly by the host), so using "window" directly is fine
            window.clearTimeout(this.heartbeatSendTimerId);
            this.heartbeatSendTimerId = undefined;
        };
        this.dispose = () => {
            // Memory cleanup: Stop the heartbeat, dispose the UserActivityTracker and set the logger undefined when dispose is called
            this.stop();
            // Exception added when eslint rule was added, this should be revisited when modifying this code
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            this.userActivityTracker?.dispose();
            // Since the heart runs outside the iframe and has a reference to the logger, when a number of components are mounted and unmounted,
            // unless the heartbeatLogger is set to undefined, the heartbeat holds onto the logger and prevents it from being garbage collected.
            // Set the logger to undefined when the heartbeat is disposed to avoid memory leaks
            this.heartbeatLogger = undefined;
        };
        this.heartbeatLogger = new NamespaceLoggerAdapter('Heartbeat', logger);
        // When the heartbeat is instantiated, immediately send one
        this.sendHeartbeatAndReQueue();
        this.userActivityTracker = new UserActivityTracker(Heartbeat.userInactiveAfterMs, this.onUserActivityStateChanged);
    }
}
// Send the heartbeat signal every 5 minutes
Heartbeat.heartbeatIntervalMs = 5 * 60 * 1000;
// Time in ms after which the user session is considered inactive, set to 5 minutes
Heartbeat.userInactiveAfterMs = 5 * 60 * 1000;
//# sourceMappingURL=Heartbeat.js.map